import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: 'Bearer ' + token() });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

const LEAVE_TYPES = [
  { value: "sick", label: "Sick Leave - Self (S)" },
  { value: "bereavement", label: "Bereavement (B)" },
  { value: "vacation", label: "Vacation (V)" },
  { value: "annual", label: "Annual Leave (A/L)" },
  { value: "overtime", label: "Overtime Credits (TO)" },
  { value: "holiday", label: "Holiday Credits (TH)" },
  { value: "excess", label: "Excess Hours Credit (TE)" },
  { value: "personal_holiday", label: "Personal Holiday (PH)" },
  { value: "plp", label: "Personal Leave Program (PLP)" },
  { value: "jury", label: "Jury Duty (J)" },
  { value: "lwop", label: "Absence w/o Pay (L,A)" },
  { value: "straight", label: "Straight Time (WO, P, HC, WE)" },
  { value: "premium", label: "Premium Time (WO, P)" },
  { value: "cto", label: "Comp Time Off (CTO)" },
  { value: "professional_development", label: "Professional Development (PD)" },
  { value: "other", label: "Other (ITO)" }
];

const LEAVE_LABEL = Object.fromEntries(LEAVE_TYPES.map(lt => [lt.value, lt.label]));
const DAY_PART_LABEL = { morning: 'Morning', afternoon: 'Afternoon' };

// Parse a YYYY-MM-DD value as a LOCAL date so Postgres DATE values don't slip a day in PT.
const toLocalDate = (d) => {
  if (!d) return null;
  const s = typeof d === 'string' ? d.slice(0, 10) : '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, day] = s.split('-').map(Number); return new Date(y, m - 1, day); }
  const f = new Date(d); return isNaN(f.getTime()) ? null : f;
};
const fmtDay = (d) => { const x = toLocalDate(d); return x ? x.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''; };
const fmtDayYear = (d) => { const x = toLocalDate(d); return x ? x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''; };
const fmtRange = (from, to) => {
  if (!from) return '';
  if (String(from).slice(0, 10) === String(to).slice(0, 10)) return fmtDayYear(from);
  return `${fmtDay(from)} – ${fmtDayYear(to)}`;
};
// Sum hours per leave type across a request's allocations → [{ type, label, hours }].
const summarizeAllocations = (allocations = []) => {
  const byType = {};
  allocations.forEach(a => { if (a.leave_type) byType[a.leave_type] = (byType[a.leave_type] || 0) + (parseFloat(a.hours) || 0); });
  return Object.entries(byType).map(([type, hours]) => ({ type, label: LEAVE_LABEL[type] || type, hours }));
};

// Status pill styling + label (handles the 'partially_approved' status).
const statusMeta = (status) => {
  switch (status) {
    case 'approved':           return { bg: '#ECFDF5', fg: '#047857', dot: '#10B981', label: 'Approved' };
    case 'partially_approved': return { bg: '#EFF6FF', fg: '#1D4ED8', dot: '#3B82F6', label: 'Partially Approved' };
    case 'denied':             return { bg: '#FEF2F2', fg: '#B91C1C', dot: '#EF4444', label: 'Denied' };
    case 'changes_requested':  return { bg: '#FFF7ED', fg: '#9A3412', dot: '#EA6F2E', label: 'Changes Requested' };
    default:                   return { bg: '#FFFBEB', fg: '#B45309', dot: '#F59E0B', label: 'Pending' };
  }
};

// Note: SUPERVISORS hardcoded constant removed 2026-05-29 — supervisor is now derived from
// the requester's staff.supervisor_id (set during onboarding) and resolved via staffList.

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position:'fixed', bottom:'32px', left:'50%', transform:'translateX(-50%)', zIndex:9999, background:'#111827', color:'#fff', padding:'13px 24px', borderRadius:'10px', boxShadow:'0 8px 32px rgba(0,0,0,0.3)', fontSize:'13px', fontWeight:'500' }}>
      {message}
    </div>
  );
}

const BTN = ({ variant, onClick, disabled, children, style }) => {
  const baseStyle = { padding:'9px 18px', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor: disabled ? 'default' : 'pointer', transition:'all 0.2s', opacity: disabled ? 0.5 : 1 };
  const variants = {
    orange: { background: ORANGE, color:'#fff' },
    white: { background:'#fff', color: NAVY, border:'1px solid #E5E7EB' },
    green: { background:'#10B981', color:'#fff' },
    red: { background:'#EF4444', color:'#fff' }
  };
  return <button style={{...baseStyle, ...variants[variant], ...style}} onClick={onClick} disabled={disabled}>{children}</button>;
};

function RequestModal({ onClose, onSubmit, currentUser, staffList = [], initialRequest = null }) {
  const editing = !!initialRequest;
  // When editing, seed the wizard straight into review from the request's existing entries.
  const seedEntries = () => (initialRequest.allocations || [])
    .map(a => ({ date: String(a.date).slice(0, 10), leave_type: a.leave_type, hours: parseFloat(a.hours) || 0, day_part: a.day_part || 'full' }))
    .sort((x, y) => x.date.localeCompare(y.date));
  const [step, setStep] = useState(editing ? 'review' : 'dates');          // wizard: 'dates' -> 'review'
  const [selectedDates, setSelectedDates] = useState(() => editing ? [...new Set(seedEntries().map(e => e.date))].sort() : []);
  const [entries, setEntries] = useState(() => editing ? seedEntries() : []); // line items: [{ date, leave_type, hours, day_part }]
  const [splitDays, setSplitDays] = useState(() => new Set()); // dates shown in the "split across types" editor
  const [escalate, setEscalate] = useState(false);     // route to skip-level (supervisor unavailable)
  const [form, setForm] = useState({ reason: editing ? (initialRequest.reason || '') : '' });
  const [saving, setSaving] = useState(false);

  // Approver is auto-derived from the org chart (staff.supervisor_id). The skip-level
  // (supervisor's supervisor) is the "Other" approver used when the supervisor is unavailable.
  const me = staffList.find(s => s.id === currentUser?.id);
  const supervisor = me?.supervisor_id ? staffList.find(s => s.id === me.supervisor_id) : null;
  const supervisorDisplay = supervisor
    ? `${supervisor.first_name} ${supervisor.last_name}`
    : (me?.supervisor_id ? 'Loading…' : 'Not assigned — contact HR');
  const skipLevel = supervisor?.supervisor_id ? staffList.find(s => s.id === supervisor.supervisor_id) : null;
  const skipLevelDisplay = skipLevel ? `${skipLevel.first_name} ${skipLevel.last_name}` : '';
  const LEAVE_LABEL = Object.fromEntries(LEAVE_TYPES.map(lt => [lt.value, lt.label]));

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const inputStyle = { width:'100%', padding:'9px 11px', border:'1px solid #E5E7EB', borderRadius:'6px', fontSize:'13px', outline:'none' };
  const labelStyle = { display:'block', fontSize:'11px', fontWeight:'600', color:'#6B7280', marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.04em' };
  // Refined controls for the review step.
  const cleanInput = { padding:'9px 11px', border:'1.5px solid #E5E7EB', borderRadius:'9px', fontSize:'13px', outline:'none', color:'#111827', background:'#fff', boxSizing:'border-box' };
  const linkBtn = { border:'none', background:'none', color: NAVY, fontSize:'12px', fontWeight:600, cursor:'pointer', padding:'4px 2px', whiteSpace:'nowrap' };
  const dashBtn = { padding:'6px 12px', borderRadius:'8px', border:`1px dashed #C7CDD6`, background:'#fff', cursor:'pointer', color: NAVY, fontSize:'12px', fontWeight:600 };
  const iconBtn = (disabled) => ({ width:'30px', height:'30px', borderRadius:'8px', border:'none', background: disabled ? '#F3F4F6' : '#FEE2E2', cursor: disabled ? 'not-allowed' : 'pointer', color:'#DC2626', fontSize:'18px', lineHeight:1, opacity: disabled ? 0.5 : 1, flexShrink:0 });
  // Segmented Full / Morning / Afternoon control.
  const Seg = ({ value, onChange }) => (
    <div style={{ display:'inline-flex', border:'1.5px solid #E5E7EB', borderRadius:'9px', overflow:'hidden', flexShrink:0 }}>
      {[['full','Full day'],['morning','Morning'],['afternoon','Afternoon']].map(([val, label], i) => {
        const active = (value || 'full') === val;
        return (
          <button key={val} type="button" onClick={() => onChange(val)}
            title={val === 'morning' ? '8:30am – 12:30pm (4h)' : val === 'afternoon' ? '12:30 – 4:30pm (4h)' : 'Full day (8h)'}
            style={{ padding:'8px 12px', border:'none', borderLeft: i ? '1.5px solid #E5E7EB' : 'none', background: active ? NAVY : '#fff', color: active ? '#fff' : '#4B5563', fontSize:'12px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            {label}
          </button>
        );
      })}
    </div>
  );

  // Move to the review step: seed one 8-hour entry per newly-selected day, keep edits to
  // days that are still selected, and drop entries for days that were de-selected.
  const goToReview = () => {
    if (selectedDates.length === 0) { alert('Please select at least one date'); return; }
    setEntries(prev => {
      const kept = prev.filter(e => selectedDates.includes(e.date));
      const have = new Set(kept.map(e => e.date));
      const added = selectedDates.filter(d => !have.has(d)).map(d => ({ date: d, leave_type: 'vacation', hours: 8, day_part: 'full' }));
      return [...kept, ...added].sort((a, b) => a.date.localeCompare(b.date));
    });
    setStep('review');
  };

  const updateEntry = (index, field, value) =>
    setEntries(es => es.map((e, i) => i === index ? { ...e, [field]: value } : e));
  // Picking a day portion also snaps hours to the standard half-day length:
  // Morning (8:30–12:30) and Afternoon (12:30–4:30) = 4h, Full day = 8h (all editable).
  const setDayPart = (index, part) =>
    setEntries(es => es.map((e, i) => i === index
      ? { ...e, day_part: part, hours: (part === 'morning' || part === 'afternoon') ? 4 : 8 }
      : e));
  const addEntryForDate = (date) => setEntries(es => {
    const out = [...es];
    let lastIdx = -1; es.forEach((e, i) => { if (e.date === date) lastIdx = i; });
    out.splice(lastIdx + 1, 0, { date, leave_type: '', hours: 0, day_part: 'full' });
    return out;
  });
  const removeEntry = (index) => setEntries(es => es.length === 1 ? es : es.filter((_, i) => i !== index));
  const setAllTypes = (lt) => setEntries(es => es.map(e => ({ ...e, leave_type: lt })));

  // Split-on-demand: a day is "split" if the user opened its editor OR it has >1 allocation.
  const isSplit = (date) => splitDays.has(date) || entries.filter(e => e.date === date).length > 1;
  const enterSplit = (date) => {
    setSplitDays(s => { const n = new Set(s); n.add(date); return n; });
    setEntries(es => es.map(e => e.date === date ? { ...e, day_part: 'full' } : e)); // custom hours ⇒ no half-day suffix
  };
  const exitSplit = (date) => {
    setSplitDays(s => { const n = new Set(s); n.delete(date); return n; });
    setEntries(es => {   // collapse back to one full-day allocation
      const seen = new Set();
      return es.filter(e => {
        if (e.date !== date) return true;
        if (seen.has(date)) return false;
        seen.add(date); return true;
      }).map(e => e.date === date ? { ...e, hours: 8, day_part: 'full' } : e);
    });
  };

  const totalHours = entries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  const distinctDays = new Set(entries.map(e => e.date)).size;
  const typeBreakdown = entries.reduce((acc, e) => {
    if (e.leave_type && parseFloat(e.hours) > 0) acc[e.leave_type] = (acc[e.leave_type] || 0) + parseFloat(e.hours);
    return acc;
  }, {});

  const handleSubmit = async () => {
    const valid = entries.filter(e => e.date && e.leave_type && parseFloat(e.hours) > 0);
    if (valid.length === 0 || valid.length !== entries.length) {
      alert('Every entry needs a leave type and hours greater than 0 (or remove it).');
      return;
    }
    if (!me?.supervisor_id) {
      alert('Your supervisor is not assigned. Please contact HR before submitting a time-off request.');
      return;
    }
    if (escalate && !skipLevel) {
      alert('No alternate approver is available above your supervisor.');
      return;
    }
    setSaving(true);
    try {
      // Backend derives dates, total hours, and the approver (skip-level if escalate).
      await onSubmit({
        allocations: valid.map(e => ({ date: e.date, leave_type: e.leave_type, hours: parseFloat(e.hours), day_part: e.day_part || 'full' })),
        reason: form.reason,
        escalate
      });
      onClose();
    } catch (e) {
      alert('Failed to submit: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  // Date selection: click a day to toggle it; press-and-drag to select (or deselect) a range.
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const [viewDate, setViewDate] = useState(() => new Date(todayStart.getFullYear(), todayStart.getMonth(), 1));
  const drag = useRef({ active: false, anchor: null, base: [], remove: false });

  // End any drag on mouseup anywhere (even outside the calendar).
  useEffect(() => {
    const end = () => { drag.current.active = false; };
    window.addEventListener('mouseup', end);
    return () => window.removeEventListener('mouseup', end);
  }, []);

  const fmt = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  // Inclusive list of YYYY-MM-DD between two dates, skipping past days.
  const datesBetween = (a, b) => {
    const [s, e] = [a, b].sort();
    const out = []; const cur = new Date(s + 'T00:00:00'); const last = new Date(e + 'T00:00:00');
    while (cur <= last) {
      if (cur >= todayStart) out.push(fmt(cur.getFullYear(), cur.getMonth(), cur.getDate()));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  };
  const applyDrag = (date) => {
    const range = datesBetween(drag.current.anchor, date);
    const set = new Set(drag.current.base);
    range.forEach(d => drag.current.remove ? set.delete(d) : set.add(d));
    setSelectedDates([...set].sort());
  };
  const startDrag = (date) => {
    // If the anchor day is already selected, the drag deselects; otherwise it selects.
    drag.current = { active: true, anchor: date, base: [...selectedDates], remove: selectedDates.includes(date) };
    applyDrag(date);
  };
  const enterDrag = (date) => { if (drag.current.active) applyDrag(date); };

  // One-year booking window: current month through 11 months out (12 months total).
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const minMonth = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
  const maxMonth = new Date(todayStart.getFullYear(), todayStart.getMonth() + 11, 1);
  const canPrev = viewDate > minMonth;
  const canNext = viewDate < maxMonth;
  const shiftMonth = (delta) => {
    const n = new Date(viewDate.getFullYear(), viewDate.getMonth() + delta, 1);
    if (n >= minMonth && n <= maxMonth) setViewDate(n);
  };

  const todayStr = fmt(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate());
  const vYear = viewDate.getFullYear();
  const vMonth = viewDate.getMonth();
  const daysInMonth = new Date(vYear, vMonth + 1, 0).getDate();
  const startDay = new Date(vYear, vMonth, 1).getDay();
  const calendar = [];
  let week = new Array(startDay).fill(null);
  for (let day = 1; day <= daysInMonth; day++) {
    week.push(fmt(vYear, vMonth, day));
    if (week.length === 7) {
      calendar.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    calendar.push(week);
  }
  // Always render 6 week rows so the calendar height is constant across months
  // (months span 4–6 weeks) — prevents the layout from jumping when paging.
  while (calendar.length < 6) calendar.push(new Array(7).fill(null));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:'12px', padding:'28px', width:'700px', maxWidth:'90vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
          <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#111827', margin:0 }}>{editing ? 'Edit Time-Off Request' : 'Request Time Off'}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:'22px' }}>×</button>
        </div>

        {step === 'dates' && (
        <div style={{ marginBottom:'20px' }}>
          <label style={labelStyle}>Step 1 of 2 — Select Date(s)</label>
          <div style={{ border:'1px solid #E5E7EB', borderRadius:'8px', padding:'12px', userSelect:'none' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
              <button type="button" onClick={() => shiftMonth(-1)} disabled={!canPrev}
                style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:'6px', width:'30px', height:'30px', cursor: canPrev ? 'pointer' : 'not-allowed', color: canPrev ? '#374151' : '#D1D5DB', fontSize:'16px', lineHeight:1 }}>‹</button>
              <div style={{ fontSize:'14px', fontWeight:'700', color:'#111827' }}>{MONTHS[vMonth]} {vYear}</div>
              <button type="button" onClick={() => shiftMonth(1)} disabled={!canNext}
                style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:'6px', width:'30px', height:'30px', cursor: canNext ? 'pointer' : 'not-allowed', color: canNext ? '#374151' : '#D1D5DB', fontSize:'16px', lineHeight:1 }}>›</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'4px', marginBottom:'8px' }}>
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(day => (
                <div key={day} style={{ textAlign:'center', fontSize:'11px', fontWeight:'600', color:'#6B7280', padding:'4px' }}>{day}</div>
              ))}
            </div>
            {calendar.map((week, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'4px', marginBottom:'4px' }}>
                {week.map((date, j) => {
                  if (!date) return <div key={j} style={{ height:'34px' }} />;
                  const isSelected = selectedDates.includes(date);
                  const isPast = new Date(date + 'T00:00:00') < todayStart;
                  const isToday = date === todayStr;
                  return (
                    <button
                      key={date}
                      type="button"
                      draggable={false}
                      onMouseDown={() => !isPast && startDrag(date)}
                      onMouseEnter={() => !isPast && enterDrag(date)}
                      disabled={isPast}
                      title={isToday ? 'Today' : undefined}
                      style={{
                        height:'34px', boxSizing:'border-box', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', lineHeight:1,
                        border: isToday ? `1px solid ${ORANGE}` : '1px solid #E5E7EB', borderRadius:'6px',
                        background: isSelected ? ORANGE : '#fff', color: isSelected ? '#fff' : isPast ? '#D1D5DB' : '#374151',
                        fontSize:'12px', fontWeight: isToday ? 700 : 400, cursor: isPast ? 'not-allowed' : 'pointer', opacity: isPast ? 0.5 : 1, userSelect:'none'
                      }}
                    >
                      {date.split('-')[2]}
                      {isToday && <span style={{ fontSize:'8px', fontWeight:600, color: isSelected ? '#fff' : ORANGE, marginTop:'1px' }}>Today</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <p style={{ fontSize:'11px', color:'#9CA3AF', margin:'8px 0 0' }}>Click a day to select, or click-and-drag across days to select a range. Drag from a selected day to deselect.</p>
          {selectedDates.length > 0 && (
            <p style={{ fontSize:'12px', color:'#6B7280', marginTop:'4px' }}>
              {selectedDates.length} day{selectedDates.length > 1 ? 's' : ''} selected
            </p>
          )}
          <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'20px' }}>
            <BTN variant="white" onClick={onClose}>Cancel</BTN>
            <BTN variant="orange" onClick={goToReview} disabled={selectedDates.length === 0}>Next: Review →</BTN>
          </div>
        </div>
        )}

        {step === 'review' && (
        <div>
          <label style={labelStyle}>Step 2 of 2 — Hours &amp; Leave</label>

          {distinctDays > 1 && (
            <div style={{ marginBottom:'10px', display:'flex', alignItems:'center', gap:'8px' }}>
              <span style={{ fontSize:'12px', color:'#6B7280' }}>Apply one type to all days:</span>
              <select value="" onChange={e => e.target.value && setAllTypes(e.target.value)} style={{ ...cleanInput, padding:'6px 10px' }}>
                <option value="">—</option>
                {LEAVE_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
              </select>
            </div>
          )}

          <div style={{ border:'1px solid #EDEDF0', borderRadius:'12px', overflow:'hidden', maxHeight:'330px', overflowY:'auto' }}>
            {(() => {
              const days = [...new Set(entries.map(e => e.date))].sort();
              return days.map((date, di) => {
                const rows = entries.map((e, idx) => ({ e, idx })).filter(x => x.e.date === date);
                const split = isSplit(date);
                const dayTotal = Math.round(rows.reduce((s, x) => s + (parseFloat(x.e.hours) || 0), 0) * 100) / 100;
                const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' });
                return (
                  <div key={date} style={{ padding:'13px 14px', borderBottom: di < days.length - 1 ? '1px solid #F1F1F3' : 'none', background:'#fff' }}>
                    {!split ? (
                      <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                        <div style={{ width:'112px', fontSize:'13px', fontWeight:600, color:'#111827' }}>{dateLabel}</div>
                        <select value={rows[0].e.leave_type} onChange={ev => updateEntry(rows[0].idx, 'leave_type', ev.target.value)} style={{ ...cleanInput, flex:'1 1 150px', minWidth:'140px' }}>
                          <option value="">Select type</option>
                          {LEAVE_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                        </select>
                        <Seg value={rows[0].e.day_part} onChange={p => setDayPart(rows[0].idx, p)} />
                        <button type="button" onClick={() => enterSplit(date)} style={linkBtn}>Split</button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px', gap:'10px' }}>
                          <div style={{ fontSize:'13px', fontWeight:600, color:'#111827' }}>{dateLabel}</div>
                          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            <span style={{ fontSize:'11.5px', color: dayTotal === 8 ? '#6B7280' : '#B45309' }}>Day total: <strong style={{ color: dayTotal === 8 ? '#111827' : '#B45309' }}>{dayTotal}h</strong></span>
                            {rows.length === 1 && <button type="button" onClick={() => exitSplit(date)} style={linkBtn}>Use full / half day</button>}
                          </div>
                        </div>
                        {rows.map(({ e, idx }) => (
                          <div key={idx} style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                            <select value={e.leave_type} onChange={ev => updateEntry(idx, 'leave_type', ev.target.value)} style={{ ...cleanInput, flex:2 }}>
                              <option value="">Select type</option>
                              {LEAVE_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                            </select>
                            <input type="number" step="0.5" min="0" value={e.hours} onChange={ev => updateEntry(idx, 'hours', ev.target.value)} style={{ ...cleanInput, width:'72px' }} title="Hours" />
                            <button type="button" onClick={() => removeEntry(idx)} disabled={rows.length === 1} title="Remove" style={iconBtn(rows.length === 1)}>×</button>
                          </div>
                        ))}
                        <button type="button" onClick={() => addEntryForDate(date)} style={dashBtn}>+ Add leave type</button>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          <div style={{ marginTop:'14px', padding:'12px 16px', background:'#F9FAFB', border:'1px solid #EDEDF0', borderRadius:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:'13px', color:'#6B7280' }}>{distinctDays} day{distinctDays !== 1 ? 's' : ''} selected</span>
              <span style={{ fontSize:'16px', fontWeight:700, color: NAVY }}>{totalHours.toFixed(totalHours % 1 ? 1 : 0)}h total</span>
            </div>
            {Object.keys(typeBreakdown).length > 0 && (
              <div style={{ fontSize:'11.5px', color:'#9CA3AF', marginTop:'6px' }}>
                {Object.entries(typeBreakdown).map(([t, h]) => `${LEAVE_LABEL[t] || t}: ${h}h`).join('  ·  ')}
              </div>
            )}
          </div>

          <div style={{ marginTop:'16px', marginBottom:'16px' }}>
            <label style={labelStyle}>Reason (Optional)</label>
            <textarea value={form.reason} onChange={e => set('reason', e.target.value)} style={{...inputStyle, minHeight:'70px', fontFamily:'inherit'}} placeholder="Provide details about your time off request..." />
          </div>

          <div style={{ marginBottom:'20px' }}>
            <label style={labelStyle}>Approver</label>
            <div style={{ ...inputStyle, background:'#F9FAFB', color: me?.supervisor_id ? '#111827' : '#9B3A1F', cursor:'default', display:'flex', alignItems:'center' }}>
              {escalate ? skipLevelDisplay : supervisorDisplay}
            </div>
            {skipLevel && (
              <label style={{ display:'flex', alignItems:'flex-start', gap:'8px', marginTop:'8px', fontSize:'12px', color:'#374151', cursor:'pointer' }}>
                <input type="checkbox" checked={escalate} onChange={e => setEscalate(e.target.checked)} style={{ marginTop:'2px' }} />
                <span>My supervisor is unavailable — route to {skipLevelDisplay} ({supervisorDisplay}'s supervisor)</span>
              </label>
            )}
            <p style={{ fontSize:'11px', color:'#6B7280', marginTop:'5px' }}>
              Routed automatically from the org chart.{skipLevel ? '' : ' No alternate approver above your supervisor.'}
            </p>
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', gap:'8px' }}>
            <BTN variant="white" onClick={() => setStep('dates')}>← Back</BTN>
            <BTN variant="orange" onClick={handleSubmit} disabled={saving}>{saving ? (editing ? "Saving..." : "Submitting...") : (editing ? "Save changes" : "Submit Request")}</BTN>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default function TimeOff({ currentUser }) {
  const [requests, setRequests] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all');
  const [denyTarget, setDenyTarget] = useState(null);   // request id being denied (opens modal)
  const [denyComment, setDenyComment] = useState('');
  const [denying, setDenying] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null); // request being reviewed for approval
  const [approveRows, setApproveRows] = useState([]);        // [{ id, date, leave_type, hours, approved }]
  const [approveComment, setApproveComment] = useState('');
  const [approveModify, setApproveModify] = useState(false); // reveal the per-day trim editor
  const [approving, setApproving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);        // request being edited by its owner
  const [deleteTarget, setDeleteTarget] = useState(null);    // request being deleted (opens confirm)
  const [deleting, setDeleting] = useState(false);
  const [changesTarget, setChangesTarget] = useState(null);  // request being sent back for changes
  const [rejectRows, setRejectRows] = useState([]);          // [{ id, date, leave_type, hours, rejected }]
  const [changesComment, setChangesComment] = useState('');
  const [requestingChanges, setRequestingChanges] = useState(false);
  const safeJSONParse = (str, fallback = []) => {
    if (!str || str === '') return fallback;
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  };


  useEffect(() => {
    fetchRequests();
    // Pull staffList so the RequestModal can resolve the current user's supervisor name.
    axios.get(API + '/staff', { headers: headers() })
      .then(res => setStaffList(res.data))
      .catch(() => setStaffList([]));
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/timeoff', { headers: headers() });
      setRequests(res.data);
    } catch (e) {
      console.error(e);
      setToast('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (data) => {
    await axios.post(API + '/timeoff', data, { headers: headers() });
    setToast('Time off request submitted!');
    fetchRequests();
  };

  // Open the review-&-approve modal, seeding each allocation's approved hours = requested.
  const openApprove = (request) => {
    const rows = (request.allocations || []).map(a => ({
      id: a.id,
      date: a.date,
      leave_type: a.leave_type,
      hours: parseFloat(a.hours) || 0,
      approved: parseFloat(a.hours) || 0,
    }));
    setApproveTarget(request);
    setApproveRows(rows);
    setApproveComment('');
    setApproveModify(false);
  };

  const setApprovedHours = (id, val) =>
    setApproveRows(rows => rows.map(r => r.id === id
      ? { ...r, approved: Math.max(0, Math.min(r.hours, val === '' ? 0 : parseFloat(val) || 0)) }
      : r));
  const toggleInclude = (id) =>
    setApproveRows(rows => rows.map(r => r.id === id
      ? { ...r, approved: r.approved > 0 ? 0 : r.hours }
      : r));
  const setApprovedType = (id, val) =>
    setApproveRows(rows => rows.map(r => r.id === id ? { ...r, leave_type: val } : r));

  const confirmApprove = async () => {
    if (!approveTarget) return;
    const approvedTotal = approveRows.reduce((s, r) => s + (r.approved || 0), 0);
    if (approvedTotal <= 0) { setToast('Nothing is approved — use Deny to reject the whole request.'); return; }
    try {
      setApproving(true);
      const resp = await axios.put(API + '/timeoff/' + approveTarget.id + '/approve',
        { allocations: approveRows.map(r => ({ id: r.id, approved_hours: r.approved, leave_type: r.leave_type })), comments: approveComment.trim() },
        { headers: headers() });
      if (resp.data?.calendar_warning) setToast(resp.data.calendar_warning);
      else if (resp.data?.status === 'partially_approved') setToast('Partially approved — the approved days were added to the CAAM calendar.');
      else setToast('Request approved — added to CAAM calendar.');
      setApproveTarget(null);
      fetchRequests();
    } catch (e) {
      setToast(e.response?.data?.error || 'Failed to approve request');
    } finally {
      setApproving(false);
    }
  };

  const confirmDeny = async () => {
    if (!denyTarget) return;
    try {
      setDenying(true);
      await axios.put(API + '/timeoff/' + denyTarget + '/deny', { comments: denyComment.trim() }, { headers: headers() });
      setToast('Request denied');
      setDenyTarget(null);
      setDenyComment('');
      fetchRequests();
    } catch (e) {
      setToast('Failed to deny request');
    } finally {
      setDenying(false);
    }
  };

  // Requester edits their own request → PUT (resets it to pending for re-review).
  const handleEditRequest = async (data) => {
    await axios.put(API + '/timeoff/' + editTarget.id, data, { headers: headers() });
    setToast('Request updated — sent back for review.');
    fetchRequests();
  };

  // Supervisor: reject specific entries and send the whole request back.
  const openRequestChanges = (request) => {
    setChangesTarget(request);
    setRejectRows((request.allocations || []).map(a => ({ id: a.id, date: a.date, leave_type: a.leave_type, hours: parseFloat(a.hours) || 0, rejected: false })));
    setChangesComment('');
  };
  const toggleReject = (id) => setRejectRows(rows => rows.map(r => r.id === id ? { ...r, rejected: !r.rejected } : r));
  const confirmRequestChanges = async () => {
    if (!changesTarget) return;
    const rejectedIds = rejectRows.filter(r => r.rejected).map(r => r.id);
    if (rejectedIds.length === 0) { setToast('Mark at least one entry to reject, or use Approve / Deny.'); return; }
    if (rejectedIds.length === rejectRows.length) { setToast('All entries rejected — use Deny to reject the whole request.'); return; }
    try {
      setRequestingChanges(true);
      await axios.put(API + '/timeoff/' + changesTarget.id + '/request-changes',
        { rejected_allocation_ids: rejectedIds, comments: changesComment.trim() }, { headers: headers() });
      setToast('Sent back to the requester for changes.');
      setChangesTarget(null);
      fetchRequests();
    } catch (e) {
      setToast(e.response?.data?.error || 'Failed to send back');
    } finally {
      setRequestingChanges(false);
    }
  };

  // Requester deletes their own request (any state; approved ones also clear calendar events).
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const resp = await axios.delete(API + '/timeoff/' + deleteTarget.id, { headers: headers() });
      setToast(resp.data?.calendar_warning || 'Request deleted.');
      setDeleteTarget(null);
      fetchRequests();
    } catch (e) {
      setToast(e.response?.data?.error || 'Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  // 'approved' tab also surfaces partially-approved requests.
  const matchesFilter = (r, f) => f === 'all' || r.status === f || (f === 'approved' && r.status === 'partially_approved');
  const filteredRequests = requests.filter(r => matchesFilter(r, filter));

  if (loading) return <div style={{ padding:'40px', textAlign:'center', color:'#9CA3AF' }}>Loading time off requests...</div>;

  return (
    <div style={{ padding:'24px 28px' }}>
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {(showRequestModal || editTarget) && (
        <RequestModal
          onClose={() => { setShowRequestModal(false); setEditTarget(null); }}
          onSubmit={editTarget ? handleEditRequest : handleSubmitRequest}
          currentUser={currentUser}
          staffList={staffList}
          initialRequest={editTarget}
        />
      )}

      {approveTarget && (() => {
        const requestedTotal = Math.round(approveRows.reduce((s, r) => s + r.hours, 0) * 100) / 100;
        const approvedTotal = Math.round(approveRows.reduce((s, r) => s + (r.approved || 0), 0) * 100) / 100;
        const isPartial = approvedTotal < requestedTotal;
        const emp = (approveTarget.employee_first || approveTarget.employee_last)
          ? `${approveTarget.employee_first || ''} ${approveTarget.employee_last || ''}`.trim() : 'Employee';
        const daysCount = new Set(approveRows.map(r => r.date)).size;
        const typeChips = Object.entries(approveRows.reduce((by, r) => { by[r.leave_type] = (by[r.leave_type] || 0) + r.hours; return by; }, {})).map(([type, hours]) => ({ type, hours }));
        const linkG = { border:'none', background:'none', color: NAVY, fontSize:'12.5px', fontWeight:600, cursor:'pointer', padding:'2px 0' };
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.45)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:9998, padding:'40px 16px', overflowY:'auto' }}
            onClick={() => { if (!approving) setApproveTarget(null); }}>
            <div style={{ background:'#fff', borderRadius:'16px', width:'100%', maxWidth:'540px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding:'20px 24px 14px', borderBottom:'1px solid #F1F1F3' }}>
                <h3 style={{ margin:0, fontSize:'17px', fontWeight:600, color: NAVY }}>Approve time off — {emp}</h3>
                <p style={{ margin:'6px 0 0', fontSize:'12.5px', color:'#6B7280' }}>
                  {approveModify ? 'Uncheck days or trim hours to approve a partial amount.' : 'Review the request, then approve as-is or modify the amount.'}
                </p>
              </div>

              {!approveModify ? (
                <div style={{ padding:'20px 24px 6px' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'13px', color:'#6B7280' }}>{daysCount} day{daysCount !== 1 ? 's' : ''} · {fmtRange(approveTarget.date_from, approveTarget.date_to)}</span>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', justifyContent:'flex-end' }}>
                      {typeChips.map(t => (
                        <span key={t.type} style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 11px', borderRadius:'9px', background:'#F3F4F6', fontSize:'13px', color:'#374151', fontWeight:600 }}>
                          {LEAVE_LABEL[t.type] || t.type}<span style={{ color: ORANGE, fontWeight:700 }}>{t.hours}h</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  {approveTarget.reason && (
                    <div style={{ marginTop:'14px', padding:'10px 12px', background:'#F9FAFB', borderRadius:'8px', fontSize:'12px', color:'#4B5563' }}>{approveTarget.reason}</div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ padding:'12px 24px 4px', maxHeight:'42vh', overflowY:'auto' }}>
                    {approveRows.map(r => {
                      const included = r.approved > 0;
                      return (
                        <div key={r.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 0', borderBottom:'1px solid #F5F5F7' }}>
                          <input type="checkbox" checked={included} onChange={() => toggleInclude(r.id)} style={{ width:'16px', height:'16px', accentColor: ORANGE, cursor:'pointer' }} />
                          <div style={{ width:'74px', fontSize:'12px', fontWeight:600, color: included ? '#111827' : '#9CA3AF' }}>
                            {new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                          </div>
                          <select value={r.leave_type} disabled={!included} onChange={e => setApprovedType(r.id, e.target.value)}
                            style={{ flex:1, minWidth:'110px', padding:'7px 9px', border:'1px solid #E5E7EB', borderRadius:'8px', fontSize:'12.5px', outline:'none', background: included ? '#fff' : '#F3F4F6', color: included ? '#374151' : '#9CA3AF' }}>
                            {LEAVE_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                          </select>
                          <span style={{ fontSize:'11px', color:'#9CA3AF', whiteSpace:'nowrap' }}>req {r.hours}h</span>
                          <input type="number" step="0.5" min="0" max={r.hours} value={r.approved} disabled={!included}
                            onChange={e => setApprovedHours(r.id, e.target.value)}
                            style={{ width:'66px', padding:'7px 9px', border:'1px solid #E5E7EB', borderRadius:'8px', fontSize:'13px', outline:'none', background: included ? '#fff' : '#F3F4F6' }} title="Approved hours" />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ padding:'8px 24px 0', display:'flex', justifyContent:'space-between', fontSize:'13px', fontWeight:700, color: isPartial ? '#1D4ED8' : '#047857' }}>
                    <span>{isPartial ? 'Partial approval' : 'Full approval'}</span>
                    <span>{approvedTotal} of {requestedTotal} hrs</span>
                  </div>
                </>
              )}

              <div style={{ padding:'12px 24px 0' }}>
                {!approveModify
                  ? <button type="button" onClick={() => setApproveModify(true)} style={linkG}>Modify amount &amp; type →</button>
                  : <button type="button" onClick={() => {
                      setApproveModify(false);
                      setApproveRows((approveTarget.allocations || []).map(a => ({ id: a.id, date: a.date, leave_type: a.leave_type, hours: parseFloat(a.hours) || 0, approved: parseFloat(a.hours) || 0 })));
                    }} style={linkG}>← Back to summary</button>}
              </div>

              <div style={{ padding:'10px 24px 4px' }}>
                <textarea value={approveComment} onChange={e => setApproveComment(e.target.value)} rows={approveModify && isPartial ? 3 : 2}
                  placeholder={approveModify && isPartial ? 'Note to the requester explaining the change (recommended)' : 'Optional note to the requester'}
                  style={{ width:'100%', padding:'10px 12px', border:'1px solid #E5E7EB', borderRadius:'8px', fontSize:'13px', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', padding:'12px 24px 20px' }}>
                <BTN variant="white" onClick={() => setApproveTarget(null)} disabled={approving}>Cancel</BTN>
                <BTN variant="green" onClick={confirmApprove} disabled={approving || approvedTotal <= 0}>
                  {approving ? 'Approving…' : (approveModify && isPartial) ? `Approve ${approvedTotal}h` : 'Approve'}
                </BTN>
              </div>
            </div>
          </div>
        );
      })()}

      {denyTarget && (
        <div
          style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9998, padding:'20px' }}
          onClick={() => { if (!denying) { setDenyTarget(null); setDenyComment(''); } }}
        >
          <div
            style={{ background:'#fff', borderRadius:'14px', padding:'24px', width:'100%', maxWidth:'420px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin:0, fontSize:'17px', fontWeight:600, color: NAVY }}>Deny time-off request</h3>
            <p style={{ margin:'6px 0 16px', fontSize:'13px', color:'#6B7280' }}>
              Add an optional note explaining the denial. The requester will see this.
            </p>
            <textarea
              value={denyComment}
              onChange={e => setDenyComment(e.target.value)}
              placeholder="Reason for denial (optional)"
              autoFocus
              rows={4}
              style={{ width:'100%', padding:'11px 12px', border:'1px solid #E5E7EB', borderRadius:'8px', fontSize:'13px', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}
            />
            <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'16px' }}>
              <BTN variant="white" onClick={() => { setDenyTarget(null); setDenyComment(''); }} disabled={denying}>Cancel</BTN>
              <BTN variant="red" onClick={confirmDeny} disabled={denying}>{denying ? 'Denying…' : 'Deny Request'}</BTN>
            </div>
          </div>
        </div>
      )}

      {changesTarget && (() => {
        const emp = (changesTarget.employee_first || changesTarget.employee_last) ? `${changesTarget.employee_first || ''} ${changesTarget.employee_last || ''}`.trim() : 'Employee';
        const firstName = emp.split(' ')[0];
        const rejectedCount = rejectRows.filter(r => r.rejected).length;
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.45)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:9998, padding:'40px 16px', overflowY:'auto' }}
            onClick={() => { if (!requestingChanges) setChangesTarget(null); }}>
            <div style={{ background:'#fff', borderRadius:'16px', width:'100%', maxWidth:'520px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding:'20px 24px 14px', borderBottom:'1px solid #F1F1F3' }}>
                <h3 style={{ margin:0, fontSize:'17px', fontWeight:600, color: NAVY }}>Request changes — {emp}</h3>
                <p style={{ margin:'6px 0 0', fontSize:'12.5px', color:'#6B7280' }}>Reject the entries that need fixing. The whole request goes back to {firstName} to revise or delete — nothing is approved and no calendar events are created.</p>
              </div>
              <div style={{ padding:'12px 24px 4px', maxHeight:'46vh', overflowY:'auto' }}>
                {rejectRows.map(r => (
                  <div key={r.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 0', borderBottom:'1px solid #F5F5F7' }}>
                    <span style={{ width:'70px', fontSize:'12px', color:'#6B7280' }}>{fmtDay(r.date)}</span>
                    <span style={{ flex:1, fontSize:'13px', fontWeight:600, color: r.rejected ? '#B91C1C' : NAVY, textDecoration: r.rejected ? 'line-through' : 'none' }}>{LEAVE_LABEL[r.leave_type] || r.leave_type}</span>
                    <span style={{ width:'44px', textAlign:'right', fontSize:'13px', fontWeight:600, color: r.rejected ? '#B91C1C' : '#374151', textDecoration: r.rejected ? 'line-through' : 'none' }}>{r.hours}h</span>
                    <div style={{ display:'inline-flex', border:'1px solid #E5E7EB', borderRadius:'8px', overflow:'hidden' }}>
                      <button type="button" onClick={() => { if (r.rejected) toggleReject(r.id); }} style={{ padding:'5px 11px', border:'none', fontSize:'11px', fontWeight:700, cursor:'pointer', background: !r.rejected ? '#ECFDF5' : '#fff', color: !r.rejected ? '#047857' : '#9CA3AF' }}>Accept</button>
                      <button type="button" onClick={() => { if (!r.rejected) toggleReject(r.id); }} style={{ padding:'5px 11px', border:'none', borderLeft:'1px solid #E5E7EB', fontSize:'11px', fontWeight:700, cursor:'pointer', background: r.rejected ? '#FEF2F2' : '#fff', color: r.rejected ? '#B91C1C' : '#9CA3AF' }}>Reject</button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding:'10px 24px 4px' }}>
                <textarea value={changesComment} onChange={e => setChangesComment(e.target.value)} rows={2}
                  placeholder="Note to the requester (why these were rejected) — recommended"
                  style={{ width:'100%', padding:'10px 12px', border:'1px solid #E5E7EB', borderRadius:'8px', fontSize:'13px', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', padding:'12px 24px 20px' }}>
                <BTN variant="white" onClick={() => setChangesTarget(null)} disabled={requestingChanges}>Cancel</BTN>
                <BTN variant="orange" onClick={confirmRequestChanges} disabled={requestingChanges || rejectedCount === 0}>{requestingChanges ? 'Sending…' : 'Request changes →'}</BTN>
              </div>
            </div>
          </div>
        );
      })()}

      {deleteTarget && (
        <div style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9998, padding:'20px' }}
          onClick={() => { if (!deleting) setDeleteTarget(null); }}>
          <div style={{ background:'#fff', borderRadius:'14px', padding:'24px', width:'100%', maxWidth:'420px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:0, fontSize:'17px', fontWeight:600, color: NAVY }}>Delete this request?</h3>
            <p style={{ margin:'8px 0 0', fontSize:'13px', color:'#6B7280' }}>
              This permanently removes your time-off request{['approved','partially_approved'].includes(deleteTarget.status) ? ' and its entries from the CAAM calendar' : ''}. This can’t be undone.
            </p>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'18px' }}>
              <BTN variant="white" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</BTN>
              <BTN variant="red" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete request'}</BTN>
            </div>
          </div>
        </div>
      )}

      <div style={{ background:'#fff', padding:'20px 24px', borderRadius:'14px', marginBottom:'20px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <div>
            <h2 style={{ fontSize:'20px', fontWeight:'600', color: NAVY, margin:0, marginBottom:'4px' }}>
              Time Off / Leave
            </h2>
            <p style={{ fontSize:'12px', color:'#6B7280', margin:0 }}>
              Submit and manage time off requests with multiple leave type allocations
            </p>
          </div>
          <BTN variant="orange" onClick={() => setShowRequestModal(true)}>
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ display:'inline', marginRight:'6px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
            </svg>
            Request Time Off
          </BTN>
        </div>

        <div style={{ display:'flex', gap:'8px', borderBottom:'2px solid #E5E7EB' }}>
          {[
            { value: 'all', label: 'All Requests' },
            { value: 'pending', label: 'Pending' },
            { value: 'changes_requested', label: 'Changes Requested' },
            { value: 'approved', label: 'Approved' },
            { value: 'denied', label: 'Denied' }
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              style={{
                padding:'8px 16px', background:'none', border:'none',
                borderBottom: filter === tab.value ? `3px solid ${ORANGE}` : '3px solid transparent',
                marginBottom:'-2px', cursor:'pointer', fontSize:'13px', fontWeight:'600',
                color: filter === tab.value ? NAVY : '#6B7280'
              }}
            >
              {tab.label} ({requests.filter(r => matchesFilter(r, tab.value)).length})
            </button>
          ))}
        </div>
      </div>

      <div style={{ display:'grid', gap:'16px' }}>
        {filteredRequests.map(request => {
          const employeeName = request.employee_first && request.employee_last 
            ? `${request.employee_first} ${request.employee_last}`
            : 'Unknown Employee';

          return (
            <div key={request.id} style={{ 
              background:'#fff', 
              border:'1px solid #E5E7EB', 
              borderRadius:'10px', 
              padding:'20px',
              boxShadow:'0 1px 3px rgba(0,0,0,0.08)'
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:'12px' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'8px' }}>
                    <h3 style={{ fontSize:'16px', fontWeight:'600', color: NAVY, margin:0 }}>
                      {employeeName}
                    </h3>
                    {(() => { const sm = statusMeta(request.status); return (
                    <span style={{
                      display:'inline-flex', alignItems:'center', gap:'6px',
                      padding:'3px 10px', borderRadius:'999px', fontSize:'11px', fontWeight:600,
                      background: sm.bg, color: sm.fg
                    }}>
                      <span style={{ width:'6px', height:'6px', borderRadius:'50%', background: sm.dot }} />
                      {sm.label}
                    </span>
                    ); })()}
                  </div>
                  
                  <div style={{ fontSize:'13px', color:'#374151', fontWeight:500, marginBottom:'10px' }}>
                    {fmtRange(request.date_from, request.date_to)}
                    {(() => {
                      const dates = safeJSONParse(request.selected_dates);
                      const parts = [...new Set((request.allocations || []).map(a => a.day_part).filter(p => p === 'morning' || p === 'afternoon'))];
                      const portion = (dates.length === 1 && parts.length === 1) ? DAY_PART_LABEL[parts[0]] : null;
                      const dayNote = dates.length > 1 ? `${dates.length} days` : null;
                      const extra = [portion, dayNote].filter(Boolean).join(' · ');
                      return extra ? <span style={{ color:'#9CA3AF', fontWeight:400 }}> · {extra}</span> : null;
                    })()}
                  </div>

                  {(() => {
                    const summary = summarizeAllocations(request.allocations || []);
                    if (summary.length === 0) {
                      return (
                        <div style={{ fontSize:'13px', color:'#6B7280' }}>
                          {LEAVE_LABEL[request.leave_type] || request.leave_type}: {request.leave_hours || request.total_hours} hours
                        </div>
                      );
                    }
                    return (
                      <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'6px' }}>
                        {summary.map(t => (
                          <span key={t.type} style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'4px 10px', borderRadius:'7px', background:'#F3F4F6', fontSize:'12px', color:'#374151', fontWeight:500 }}>
                            {t.label}<span style={{ color: ORANGE, fontWeight:600 }}>{t.hours}h</span>
                          </span>
                        ))}
                        <span style={{ fontSize:'12px', color:'#6B7280', marginLeft:'2px' }}>
                          {request.status === 'partially_approved' && request.approved_hours != null
                            ? `· ${request.approved_hours} of ${request.total_hours || 0} hrs approved`
                            : `· ${request.total_hours || 0} hrs total`}
                        </span>
                      </div>
                    );
                  })()}

                  {request.reason && (
                    <div style={{ marginTop:'12px', padding:'12px', background:'#F9FAFB', borderRadius:'6px', fontSize:'12px', color:'#374151' }}>
                      <strong>Reason:</strong> {request.reason}
                    </div>
                  )}

                  {request.supervisor_comments && (
                    <div style={{ marginTop:'8px', padding:'12px', background:'#FEF3C7', borderRadius:'6px', fontSize:'12px', color:'#92400E' }}>
                      <strong>Supervisor Comments:</strong> {request.supervisor_comments}
                    </div>
                  )}

                  {request.status === 'changes_requested' && (request.allocations || []).some(a => a.rejected) && (
                    <div style={{ marginTop:'8px', padding:'10px 12px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'6px', fontSize:'12px', color:'#991B1B' }}>
                      <strong>Rejected by supervisor:</strong>{' '}
                      {(request.allocations || []).filter(a => a.rejected).map((a, i) => (
                        <span key={a.id}>{i > 0 ? ', ' : ''}{fmtDay(a.date)} · {LEAVE_LABEL[a.leave_type] || a.leave_type} · {parseFloat(a.hours)}h</span>
                      ))}
                      <span> — edit and resubmit, or delete the request.</span>
                    </div>
                  )}

                  <div style={{ marginTop:'12px', fontSize:'11px', color:'#9CA3AF' }}>
                    Submitted {fmtDayYear(request.created_at)}
                    {request.supervisor_name && ` · Approver: ${request.supervisor_name}`}
                  </div>
                </div>

                {request.status === 'pending' && request.employee_id !== currentUser?.id && (request.supervisor_id === currentUser?.id || currentUser?.role === 'admin') && (
                  <div style={{ display:'flex', gap:'8px', marginLeft:'16px', flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end', maxWidth:'270px' }}>
                    <BTN variant="green" onClick={() => openApprove(request)} style={{ fontSize:'12px', padding:'7px 16px' }}>
                      Review &amp; Approve
                    </BTN>
                    <BTN variant="white" onClick={() => openRequestChanges(request)} style={{ fontSize:'12px', padding:'7px 16px', color:'#9A3412', borderColor:'#FAD9BE' }}>
                      Request changes
                    </BTN>
                    <BTN variant="white" onClick={() => setDenyTarget(request.id)} style={{ fontSize:'12px', padding:'7px 16px', color:'#B91C1C', borderColor:'#FCA5A5' }}>
                      Deny
                    </BTN>
                  </div>
                )}

                {request.employee_id === currentUser?.id && (
                  <div style={{ display:'flex', gap:'8px', marginLeft:'16px', flexShrink:0 }}>
                    {['pending', 'changes_requested', 'denied'].includes(request.status) && (
                      <BTN variant="white" onClick={() => setEditTarget(request)} style={{ fontSize:'12px', padding:'7px 16px' }}>
                        Edit
                      </BTN>
                    )}
                    <BTN variant="white" onClick={() => setDeleteTarget(request)} style={{ fontSize:'12px', padding:'7px 16px', color:'#B91C1C', borderColor:'#FCA5A5' }}>
                      Delete
                    </BTN>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {filteredRequests.length === 0 && (
          <div style={{ 
            textAlign:'center', 
            padding:'60px 20px',
            background:'#fff',
            borderRadius:'12px',
            border:'1px solid #E5E7EB'
          }}>
            <svg width="64" height="64" fill="none" stroke="#D1D5DB" viewBox="0 0 24 24" style={{ margin:'0 auto 16px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <p style={{ fontSize:'14px', fontWeight:'600', color:'#6B7280', marginBottom:'8px' }}>
              No {filter !== 'all' ? filter : ''} requests found
            </p>
            <p style={{ fontSize:'12px', color:'#9CA3AF' }}>
              Submit a time off request to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
